import type { FormVariant } from "@homeinstead/analytics";
import type { CareSeekerShallowLeadFormData } from "~www/types/forms/care-seeker-shallow-lead-form";
import type { HeroVariant } from "./shared/hero-variants";

import { ServiceLocationTrackedClickEvent } from "@homeinstead/analytics";
import { useOfficeSearch } from "@homeinstead/geolocation/hooks";
import { mapNearbyOrganizationsToOfficeOptions } from "@homeinstead/marketing-api";
import { cn } from "@homeinstead/ui";
import { FormError } from "@homeinstead/ui/components/FormError";
import { Input } from "@homeinstead/ui/components/Input";
import { OfficeSelect } from "@homeinstead/ui/components/OfficeSelect";
import { AnimatePresence, motion } from "motion/react";
import React, { Suspense } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { usePostalCodeAnalytics } from "~www/hooks/usePostalCodeAnalytics";
import ContinueButton from "./shared/ContinueButton";
import { getHeroVariantStyles } from "./shared/hero-variants";
import { Testimony } from "./Testimony";
import { testimonies } from "./testimonies";

const OfficeStep = ({
  nextStep,
  className,
  variant = "default",
  formVariant,
}: {
  className?: string;
  nextStep: () => void;
  variant?: HeroVariant;
  formVariant: FormVariant;
}) => {
  const { register, trigger, watch, control, getFieldState } =
    useFormContext<CareSeekerShallowLeadFormData>();

  // This is the raw user input (postal code typed by user)
  const location = watch("location", "");

  const finalPostalCode = watch("postalCode", "");

  const franchiseNumber = watch("organizationNumber", 0);

  const { invalid: invalidInput } = getFieldState("location");

  const { textColorClass, continueButtonVariant, errorColorClass } =
    getHeroVariantStyles(variant);

  const {
    data: officeSearch,
    isFetching: isFetchingOffices,
    error: officeSearchError,
  } = useOfficeSearch({
    location: location, // pass in the user typed code
  });

  const officeOptions = React.useMemo(
    () =>
      mapNearbyOrganizationsToOfficeOptions(officeSearch?.organizations ?? []),
    [officeSearch],
  );

  const trackingEvent = {
    eventName: "tracked_click",
    eventPayloadGenerator: () => {
      return ServiceLocationTrackedClickEvent(
        finalPostalCode.trim() === "" ? undefined : Number(finalPostalCode),
        Number(franchiseNumber),
        formVariant,
      );
    },
  };

  const officeUserSelection: Record<string, string> = {};
  if (finalPostalCode.trim()) {
    officeUserSelection.postalCode = finalPostalCode.trim();
  }
  if (franchiseNumber) {
    officeUserSelection.franchiseNumber = String(franchiseNumber);
  }
  const officeLeadFormStep = {
    formVariant,
    stepName: "office",
    userSelection: officeUserSelection,
  };

  // For the continue button
  const userPostalCodeValid = !!location.trim() && !invalidInput;
  const franchiseNumberValid = franchiseNumber !== 0;
  const officesAvailable = officeOptions.length > 0;
  const formReady =
    userPostalCodeValid && franchiseNumberValid && officesAvailable;

  const shouldShowOfficeSelect =
    !!location && // user typed something
    !invalidInput && // user’s input is not invalid
    !officeSearchError;

  usePostalCodeAnalytics({
    error: officeSearchError,
    formVariant,
    isFetching: isFetchingOffices,
    isValid: userPostalCodeValid,
    locationsAvailable: officesAvailable,
    postalCode: location.trim(),
  });

  return (
    <div
      className={cn(
        "flex h-full flex-col gap-4 gap-y-24 xl:flex-row xl:gap-y-4",
        className,
      )}
    >
      <div className="flex w-full flex-col gap-4 xl:max-w-[440px]">
        <h1
          className={cn(
            "text-left font-bold font-jakarta",
            "text-xl md:text-1xl",
            textColorClass,
          )}
        >
          Where is care needed?
        </h1>
        <p className={cn("mb-4 text-base", textColorClass)}>
          Please enter the location where in-home care is needed.
        </p>

        <div className="max-w-[440px]">
          <Controller
            control={control}
            name="location"
            render={({ field, fieldState }) => {
              return (
                <div className="max-w-[440px]">
                  <input
                    type="hidden"
                    value={finalPostalCode}
                    {...register("postalCode")}
                  />
                  <Input
                    className="peer bg-white text-base text-new-beginnings-base"
                    placeholder="Postal Code"
                    type="text"
                    {...field}
                    // Override onChange to trigger validation on each keystroke
                    onChange={(event) => {
                      field.onChange(event);
                      trigger("location");
                    }}
                  />
                  <input
                    type="hidden"
                    value={franchiseNumber}
                    {...register("organizationNumber")}
                  />
                  {fieldState.error ? (
                    <FormError.span
                      className={cn("mt-2 max-w-[440px]", errorColorClass)}
                    >
                      {fieldState.error.message}
                    </FormError.span>
                  ) : null}
                </div>
              );
            }}
          />
        </div>

        <Suspense
          fallback={
            <p className={cn("w-full", textColorClass)}>Loading offices...</p>
          }
        >
          {isFetchingOffices && shouldShowOfficeSelect ? (
            <p className={textColorClass}>Loading offices...</p>
          ) : officeSearchError ? (
            <FormError.p className={errorColorClass}>
              Failed to load offices.
            </FormError.p>
          ) : shouldShowOfficeSelect ? (
            <AnimatePresence mode="wait">
              {officeOptions.length > 0 ? (
                <motion.div
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -5 }}
                  initial={{ opacity: 0, y: -5 }}
                  key="office-select"
                  transition={{ duration: 0.2, ease: "easeInOut" }}
                >
                  <OfficeSelect
                    officeIdFieldName={"organizationNumber"}
                    officeOptions={officeOptions}
                    postalCodeFieldName={"postalCode"}
                  />
                </motion.div>
              ) : location ? (
                <motion.p
                  animate={{ opacity: 1, y: 0 }}
                  className="text-error text-sm"
                  exit={{ opacity: 0, y: -5 }}
                  initial={{ opacity: 0, y: -5 }}
                  key="no-offices"
                  transition={{ duration: 0.2, ease: "easeInOut" }}
                >
                  No offices found.
                </motion.p>
              ) : null}
            </AnimatePresence>
          ) : null}
        </Suspense>

        {formReady ? (
          <ContinueButton
            isEnabled
            leadFormStep={officeLeadFormStep}
            nextStep={nextStep}
            trackingEvent={trackingEvent}
            variant={continueButtonVariant}
          />
        ) : null}
      </div>

      <Testimony
        className="mb-auto ml-auto flex max-w-md flex-col gap-4"
        variant={variant}
      >
        {testimonies.step4}
      </Testimony>
    </div>
  );
};

export default OfficeStep;
